import FingerprintJS from '@fingerprintjs/fingerprintjs'
import FingerprintJSPro from '@fingerprintjs/fingerprintjs-pro'

export const FINGERPRINT_KEY = 'fingerprint_device_id_v1'
const API_KEY = 'gplK28gq58Z3WpUTOXuX'
const REGION = 'ap'

// 设备类型前缀
const PREFIX = {
  PC: {
    PRO: 'P_', // PC端专业版
    NORMAL: 'Pn_', // PC端普通版
  },
  H5: {
    PRO: 'H_', // H5端专业版
    NORMAL: 'Hn_', // H5端普通版
  },
}

// 获取设备类型前缀
const getDevicePrefix = (isPro: boolean) => {
  const deviceType = isMobile() ? 'H5' : 'PC'
  return PREFIX[deviceType][isPro ? 'PRO' : 'NORMAL']
}

// 判断是否为移动设备
export const isMobile = () => {
  if (typeof navigator === 'undefined') return false // 預設不是 mobile
  return /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
    navigator.userAgent
  )
}

const isIos = () => {
  return !!window?.WebViewJavascriptBridge
}

const isAndroid = () => {
  return !!window?.hotCoin?.bridgeNative
}

// 从App Bridge获取设备ID
const getAppDeviceId = async (): Promise<string | undefined> =>
  new Promise((resolve) => {
    try {
      if (!window?.hotCoin?.bridgeDeviceIdHandler) {
        resolve(undefined)
        return
      }
      const deviceId = JSON.parse(window.hotCoin.bridgeDeviceIdHandler() || '{}')?.deviceId
      if (deviceId) {
        localStorage.setItem(FINGERPRINT_KEY, deviceId)
      }
      resolve(deviceId)
    } catch (e) {
      console.error('获取安卓设备ID失败:', e)
      resolve(undefined)
    }
  })

// 添加混淆工具函数
const generateRandomStr = (length: number): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  let result = ''
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return result
}

// 混淆 visitorId
const obfuscateVisitorId = (visitorId: string): string => {
  const length = visitorId.length
  const randomStr = generateRandomStr(length)

  // 交替混合原始visitorId和随机字符串
  let result = ''
  for (let i = 0; i < length; i++) {
    result += visitorId[i] + randomStr[i]
  }

  return result.slice(0, length) // 保持原始长度
}

// 使用普通版本的指纹识别
const useNormalFingerprint = async (): Promise<string | undefined> => {
  try {
    const fp = await FingerprintJS.load()
    const { visitorId } = await fp.get()
    if (visitorId) {
      // 对 visitorId 进行混淆
      const obfuscatedId = obfuscateVisitorId(visitorId)
      // 添加设备类型前缀
      const deviceId = `${getDevicePrefix(false)}${obfuscatedId}`
      localStorage.setItem(FINGERPRINT_KEY, deviceId)
      return deviceId
    }
    throw new Error('fingerprint error: visitorId is null')
  } catch (e) {
    console.error('fingerprint error:', e)
    return undefined
  }
}

// 使用专业版指纹识别
const useProFingerprint = async (): Promise<string | undefined> => {
  try {
    const pattern = /^(www|mt|wt|[a-zA-Z0-9-]+)\.([a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+)$/
    const replacement = 'metrics.$2'
    const metricsEndPoint = location.hostname.replace(pattern, replacement)
    const fp = await FingerprintJSPro.load({
      apiKey: API_KEY,
      region: REGION,
      endpoint: [`https://${metricsEndPoint}`, FingerprintJSPro.defaultEndpoint],
      scriptUrlPattern: `https://${metricsEndPoint}/web/v<version>/<apiKey>/loader_v<loaderVersion>.js`,
    })
    const { visitorId } = await fp.get()
    if (visitorId) {
      // PC端设备id需拼接前缀 P_
      const deviceId = `${getDevicePrefix(true)}${visitorId}`
      localStorage.setItem(FINGERPRINT_KEY, deviceId)
      return deviceId
    }
    throw new Error('fingerprintPro error: visitorId is null')
  } catch (e) {
    console.error('fingerprint pro error:', e)
    // 降级使用普通版本
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useNormalFingerprint()
  }
}

let getDeviceIdPromise: Promise<string | undefined> | null = null

const getDeviceId = async (): Promise<string | undefined> => {
  // 服务端渲染时直接返回
  if (typeof window === 'undefined') return undefined

  // 复用已有的Promise
  if (getDeviceIdPromise) return getDeviceIdPromise

  getDeviceIdPromise = new Promise(async (resolve) => {
    // 尝试从App Bridge获取设备ID
    if (isIos() || isAndroid()) {
      const deviceId = await getAppDeviceId()
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      deviceId && localStorage.setItem(FINGERPRINT_KEY, deviceId)
      resolve(deviceId)
      return
    }

    // 尝试从localStorage获取
    const deviceId = localStorage.getItem(FINGERPRINT_KEY)
    if (deviceId) {
      resolve(deviceId)
      return
    }

    // 使用requestIdleCallback在浏览器空闲时执行指纹识别
    if (window.requestIdleCallback) {
      window.requestIdleCallback(
        async () => {
          const id = await useProFingerprint()
          resolve(id)
        },
        { timeout: 2000 } // 设置超时时间，确保即使浏览器一直忙碌也最终会执行
      )
    } else {
      // 降级处理，对于不支持requestIdleCallback的浏览器
      setTimeout(async () => {
        const id = await useProFingerprint()
        resolve(id)
      }, 2000)
    }
  })

  return getDeviceIdPromise
}

export default getDeviceId
